import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, X, ArrowLeft, Printer, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

const inputCls =
  "w-full bg-black border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-[#A6A6B0]/50 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#6D00FF] focus:ring-1 focus:ring-[#6D00FF]/60";

const STATUS_CLS = {
  draft: "text-[#A6A6B0] border border-white/10",
  sent: "bg-[#6D00FF]/20 text-[#E8B6FF]",
  paid: "bg-emerald-500/15 text-emerald-300",
};

const today = () => new Date().toISOString().slice(0, 10);

const InvoiceView = ({ invoice, onBack, onStatus, onDelete }) => (
  <div data-testid="invoice-view">
    <div className="flex flex-wrap items-center justify-between gap-3 mb-6 print:hidden">
      <button
        data-testid="invoice-back"
        onClick={onBack}
        className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-[#A6A6B0] hover:text-white transition-colors duration-300"
      >
        <ArrowLeft className="w-4 h-4" /> All Invoices
      </button>
      <div className="flex items-center gap-2">
        {invoice.status === "draft" && (
          <button
            data-testid="invoice-mark-sent"
            onClick={() => onStatus("sent")}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] border border-[#8B2CFF]/50 text-[#E8B6FF] hover:bg-[#6D00FF]/20 rounded-md px-4 py-2.5 transition-colors duration-300"
          >
            <Send className="w-3.5 h-3.5" /> Mark Sent
          </button>
        )}
        {invoice.status !== "paid" && (
          <button
            data-testid="invoice-mark-paid"
            onClick={() => onStatus("paid")}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 rounded-md px-4 py-2.5 transition-colors duration-300"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Mark Paid
          </button>
        )}
        <button
          data-testid="invoice-print"
          onClick={() => window.print()}
          className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] bg-[#6D00FF] hover:bg-[#8B2CFF] text-white rounded-md px-4 py-2.5 transition-colors duration-300"
        >
          <Printer className="w-3.5 h-3.5" /> Print / PDF
        </button>
        <button
          data-testid="invoice-delete"
          onClick={onDelete}
          className="p-2.5 text-[#A6A6B0] hover:text-[#C43CFF] transition-colors duration-300"
          aria-label="Delete invoice"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>

    <div className="invoice-print-area bg-white text-black rounded-lg overflow-hidden shadow-2xl">
      <div className="p-8 sm:p-12">
        <div className="flex flex-wrap items-start justify-between gap-6 pb-8 border-b border-black/10">
          <div>
            <div className="inline-block bg-[#050507] rounded-md px-4 py-3 mb-3">
              <img src="/images/brandzenith-logo.png" alt="BrandZenith" className="h-8 w-auto" />
            </div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-black/50">
              AI-Powered Brand Growth Studio
            </p>
          </div>
          <div className="text-right">
            <p className="font-display text-3xl font-extrabold tracking-tight text-black">INVOICE</p>
            <p className="text-sm font-bold text-[#6D00FF] mt-1">{invoice.number}</p>
            <span
              className={`inline-block mt-2 text-[10px] font-bold uppercase tracking-[0.16em] rounded px-2.5 py-1 ${
                invoice.status === "paid"
                  ? "bg-emerald-100 text-emerald-700"
                  : invoice.status === "sent"
                    ? "bg-purple-100 text-[#6D00FF]"
                    : "bg-black/5 text-black/60"
              }`}
            >
              {invoice.status}
            </span>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 py-8">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 mb-2">Billed To</p>
            <p className="font-semibold">{invoice.client_name}</p>
            {invoice.client_email && <p className="text-sm text-black/60 mt-1">{invoice.client_email}</p>}
          </div>
          <div className="sm:text-right">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 mb-2">Dates</p>
            <p className="text-sm">Issued: {invoice.issue_date}</p>
            {invoice.due_date && <p className="text-sm mt-1">Due: {invoice.due_date}</p>}
          </div>
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-black/80 text-left">
              <th className="py-3 pr-4 text-[10px] font-bold uppercase tracking-[0.18em]">Description</th>
              <th className="py-3 px-4 text-[10px] font-bold uppercase tracking-[0.18em] text-right">Qty</th>
              <th className="py-3 px-4 text-[10px] font-bold uppercase tracking-[0.18em] text-right">Rate</th>
              <th className="py-3 pl-4 text-[10px] font-bold uppercase tracking-[0.18em] text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((item, i) => (
              <tr key={i} className="border-b border-black/10">
                <td className="py-3.5 pr-4">{item.description}</td>
                <td className="py-3.5 px-4 text-right">{item.quantity}</td>
                <td className="py-3.5 px-4 text-right">{fmt(item.rate)}</td>
                <td className="py-3.5 pl-4 text-right font-semibold">{fmt(item.quantity * item.rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end mt-6">
          <div className="w-full sm:w-64 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-black/60">Subtotal</span>
              <span>{fmt(invoice.subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-black/60">Tax ({invoice.tax_percent}%)</span>
              <span>{fmt(invoice.tax_amount)}</span>
            </div>
            <div className="flex justify-between border-t-2 border-black/80 pt-2">
              <span className="font-bold">Total</span>
              <span className="font-bold text-[#6D00FF] text-lg">{fmt(invoice.total)}</span>
            </div>
          </div>
        </div>

        {invoice.notes && (
          <div className="mt-8 pt-6 border-t border-black/10">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 mb-2">Notes</p>
            <p className="text-sm text-black/70 whitespace-pre-wrap">{invoice.notes}</p>
          </div>
        )}

        <p className="mt-10 text-[11px] text-black/40 text-center">
          BrandZenith — Make Your Next Move. · brandzenith.studio
        </p>
      </div>
    </div>
  </div>
);

export const AdminInvoices = () => {
  const [invoices, setInvoices] = useState(null);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    client_id: "",
    client_name: "",
    client_email: "",
    issue_date: today(),
    due_date: "",
    tax_percent: 0,
    notes: "",
    status: "draft",
  });
  const [items, setItems] = useState([{ description: "", quantity: 1, rate: 0 }]);

  const load = async () => {
    const [inv, cl] = await Promise.all([
      axios.get(`${API}/invoices`, { withCredentials: true }),
      axios.get(`${API}/clients`, { withCredentials: true }),
    ]);
    setInvoices(inv.data);
    setClients(cl.data);
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickClient = (e) => {
    const c = clients.find((cl) => cl.id === e.target.value);
    setForm((f) => ({
      ...f,
      client_id: c ? c.id : "",
      client_name: c ? (c.company ? `${c.name} (${c.company})` : c.name) : f.client_name,
      client_email: c?.email || "",
    }));
  };

  const setItem = (i, k, v) =>
    setItems((rows) => rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const subtotal = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.rate) || 0), 0);
  const tax = (subtotal * (Number(form.tax_percent) || 0)) / 100;

  const save = async (e) => {
    e.preventDefault();
    const cleanItems = items
      .filter((i) => i.description.trim())
      .map((i) => ({ description: i.description.trim(), quantity: Number(i.quantity) || 1, rate: Number(i.rate) || 0 }));
    if (!cleanItems.length) {
      toast.error("Add at least one line item");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/invoices`,
        {
          client_id: form.client_id || null,
          client_name: form.client_name,
          client_email: form.client_email || null,
          items: cleanItems,
          tax_percent: Number(form.tax_percent) || 0,
          issue_date: form.issue_date,
          due_date: form.due_date || null,
          notes: form.notes || null,
          status: form.status,
        },
        { withCredentials: true }
      );
      toast.success(`Invoice ${data.number} created`);
      setShowForm(false);
      setForm({ client_id: "", client_name: "", client_email: "", issue_date: today(), due_date: "", tax_percent: 0, notes: "", status: "draft" });
      setItems([{ description: "", quantity: 1, rate: 0 }]);
      await load();
      setViewing(data);
    } catch {
      toast.error("Could not create invoice");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (status) => {
    const { data } = await axios.patch(
      `${API}/invoices/${viewing.id}`,
      { status },
      { withCredentials: true }
    );
    setViewing(data);
    setInvoices((inv) => inv.map((i) => (i.id === data.id ? data : i)));
    toast.success(`Invoice marked ${status}`);
  };

  const remove = async () => {
    if (!window.confirm(`Delete invoice ${viewing.number}?`)) return;
    await axios.delete(`${API}/invoices/${viewing.id}`, { withCredentials: true });
    toast.success("Invoice deleted");
    setViewing(null);
    load();
  };

  if (!invoices) return <p className="text-sm text-[#A6A6B0]">Loading invoices…</p>;

  if (viewing)
    return <InvoiceView invoice={viewing} onBack={() => setViewing(null)} onStatus={updateStatus} onDelete={remove} />;

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight">
            Invoices
          </h1>
          <p className="text-sm text-[#A6A6B0] mt-2">{invoices.length} issued</p>
        </div>
        <button
          data-testid="invoice-add-button"
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-[#6D00FF] hover:bg-[#8B2CFF] text-white text-xs font-bold uppercase tracking-[0.14em] px-5 py-3 rounded-md transition-colors duration-300"
        >
          <Plus className="w-4 h-4" /> New Invoice
        </button>
      </div>

      {showForm && (
        <form data-testid="invoice-form" onSubmit={save} className="glass-card rounded-lg p-6 sm:p-8 mb-8 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-bold uppercase tracking-tight">New Invoice</h2>
            <button type="button" onClick={() => setShowForm(false)} aria-label="Close form">
              <X className="w-5 h-5 text-[#A6A6B0] hover:text-white" />
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <select data-testid="invoice-client-select" value={form.client_id} onChange={pickClient} className={inputCls}>
              <option value="">— Select CRM client (optional) —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id} className="bg-black">
                  {c.name}
                  {c.company ? ` (${c.company})` : ""}
                </option>
              ))}
            </select>
            <input data-testid="invoice-client-name" required value={form.client_name} onChange={set("client_name")} placeholder="Client name *" className={inputCls} />
            <input data-testid="invoice-client-email" type="email" value={form.client_email} onChange={set("client_email")} placeholder="Client email" className={inputCls} />
            <div className="grid grid-cols-2 gap-4">
              <input data-testid="invoice-issue-date" type="date" required value={form.issue_date} onChange={set("issue_date")} className={inputCls} />
              <input data-testid="invoice-due-date" type="date" value={form.due_date} onChange={set("due_date")} className={inputCls} />
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#A6A6B0] mb-3">Line Items</p>
            <div className="space-y-3">
              {items.map((item, i) => (
                <div key={i} className="grid grid-cols-[1fr_80px_120px_40px] gap-3 items-center" data-testid={`invoice-item-row-${i}`}>
                  <input value={item.description} onChange={(e) => setItem(i, "description", e.target.value)} placeholder="Description" className={inputCls} />
                  <input type="number" min="0" step="any" value={item.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} placeholder="Qty" className={inputCls} />
                  <input type="number" min="0" step="any" value={item.rate} onChange={(e) => setItem(i, "rate", e.target.value)} placeholder="Rate" className={inputCls} />
                  <button type="button" onClick={() => setItems((r) => r.filter((_, idx) => idx !== i))} className="p-2 text-[#A6A6B0] hover:text-[#C43CFF]" aria-label="Remove item">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              data-testid="invoice-add-item"
              onClick={() => setItems((r) => [...r, { description: "", quantity: 1, rate: 0 }])}
              className="mt-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8B2CFF] hover:text-[#C43CFF] transition-colors duration-300"
            >
              <Plus className="w-3.5 h-3.5" /> Add line item
            </button>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 items-end">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-[#A6A6B0] mb-2">Tax %</label>
              <input data-testid="invoice-tax" type="number" min="0" step="any" value={form.tax_percent} onChange={set("tax_percent")} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-[#A6A6B0] mb-2">Status</label>
              <select data-testid="invoice-status-select" value={form.status} onChange={set("status")} className={inputCls}>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            <div className="text-right">
              <p className="text-xs text-[#A6A6B0]">Subtotal {fmt(subtotal)} · Tax {fmt(tax)}</p>
              <p className="font-display text-2xl font-extrabold text-gradient-purple mt-1">{fmt(subtotal + tax)}</p>
            </div>
          </div>

          <textarea data-testid="invoice-notes" rows={2} value={form.notes} onChange={set("notes")} placeholder="Notes — payment terms, advance received, etc." className={`${inputCls} resize-none`} />

          <button
            data-testid="invoice-save-button"
            type="submit"
            disabled={saving}
            className="bg-[#6D00FF] hover:bg-[#8B2CFF] disabled:opacity-60 text-white text-xs font-bold uppercase tracking-[0.14em] px-6 py-3 rounded-md transition-colors duration-300"
          >
            {saving ? "Creating…" : "Create Invoice"}
          </button>
        </form>
      )}

      {invoices.length === 0 ? (
        <div className="glass-card rounded-lg p-16 text-center">
          <p className="text-[#A6A6B0] text-sm">No invoices yet. Create your first one.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="invoices-list">
          {invoices.map((inv, i) => (
            <button
              key={inv.id}
              data-testid={`invoice-item-${i}`}
              onClick={() => setViewing(inv)}
              className="w-full text-left glass-card rounded-lg px-5 py-4 flex flex-wrap items-center justify-between gap-3 hover:border-[#6D00FF]/40 transition-colors duration-300"
            >
              <div>
                <p className="text-sm font-semibold">
                  <span className="text-[#8B2CFF]">{inv.number}</span> · {inv.client_name}
                </p>
                <p className="text-xs text-[#A6A6B0] mt-0.5">
                  Issued {inv.issue_date}
                  {inv.due_date ? ` · Due ${inv.due_date}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-display font-bold tracking-tight">{fmt(inv.total)}</span>
                <span className={`text-[10px] font-bold uppercase tracking-[0.14em] rounded px-2.5 py-1 ${STATUS_CLS[inv.status] || STATUS_CLS.draft}`}>
                  {inv.status}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
